export function formatShortId(value: string): string {
  const [firstPart] = value.split('-');
  return firstPart || value;
}

export function formatDateTime(value: string | null): string {
  if (!value) {
    return 'Never';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(new Date(value));
}

export function formatStatus(value: string): string {
  return value.replace('_', ' ');
}

export function formatLanguages(values: string[]): string {
  return values.length > 0 ? values.join(', ') : 'Not available yet';
}

export function formatInterviewLimit(value: number | null): string {
  return value === null ? 'No limit' : String(value);
}

export function formatParticipantRecordingPrivacyMode(
  value: 'transparent' | 'obfuscated' | 'hidden'
): string {
  if (value === 'obfuscated') return 'Obfuscated';
  if (value === 'hidden') return 'Hidden';
  return 'Transparent';
}

export function formatEnabledState(value: boolean): string {
  return value ? 'Enabled' : 'Disabled';
}

export function formatOwnerDisplayName(project: {
  ownerName: string | null;
  ownerEmail: string | null;
}): string {
  return project.ownerName ?? project.ownerEmail ?? 'Unassigned';
}

export interface RelativeTimeDisplay {
  value: string;
  suffix: string | null;
}

export function formatRelativeTimeFromNow(
  value: string | null,
  now = new Date()
): RelativeTimeDisplay {
  if (!value) {
    return { value: 'Never', suffix: null };
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return { value: 'Never', suffix: null };
  }

  const secondsAgo = Math.max(0, Math.floor((now.getTime() - date.getTime()) / 1000));

  if (secondsAgo < 60) {
    return { value: formatRelativeUnit(secondsAgo, 'second'), suffix: 'ago' };
  }

  const minutesAgo = Math.floor(secondsAgo / 60);
  if (minutesAgo < 60) {
    return { value: formatRelativeUnit(minutesAgo, 'minute'), suffix: 'ago' };
  }

  const hoursAgo = Math.floor(minutesAgo / 60);
  if (hoursAgo < 24) {
    return { value: formatRelativeUnit(hoursAgo, 'hour'), suffix: 'ago' };
  }

  return { value: formatRelativeUnit(Math.floor(hoursAgo / 24), 'day'), suffix: 'ago' };
}

function formatRelativeUnit(value: number, unit: string): string {
  return `${value} ${unit}${value === 1 ? '' : 's'}`;
}

export function formatProductLine(value: string | null | undefined): string {
  if (value === undefined) return 'Not available';
  if (value === 'insights') return 'Insights';
  if (value === 'customer_success') return 'Customer Success';
  return 'Unknown';
}

export { formatVoiceProvider } from '@/lib/voice-provider';
